import React, {useState, useEffect, useRef} from 'react';
import {
  TaxIdentificationNumber,
  TaxIdentificationNumberWithNoAgentNumbers,
  matchTaxIdentificationNumber,
  ValidatingTaxIdentificationNumber,
} from 'Users/models/User/Agent/TaxIdentificationNumber';
import {FormControl, TextField} from '@material-ui/core';
import ErrorPopover, {Errors} from 'Shared/components/ErrorPopover';
import Loading from 'Shared/components/Loading';
import {maskTaxId, unMaskTaxId} from 'Shared/utilities/taxIdMasker';

const getTaxIdErrors = (taxId: TaxIdentificationNumber) =>
  matchTaxIdentificationNumber(taxId, {
    ExistingTaxIdentificationNumber: () => [],
    InvalidTaxIdentificationNumber: () => ['Please enter a valid tax ID number'],
    ValidTaxIdentificationNumber: () => [],
    TaxIdentificationNumberWithNoAgentNumbers: () => [
      'The tax ID you entered could not be found. Please verify this is the correct number and try again.',
    ],
    UnvalidatedTaxIdentificationNumber: () => ['Please enter a valid tax ID number'],
    ValidatingTaxIdentificationNumber: () => [],
  });
const isInTaxIdErrorState = (taxId: TaxIdentificationNumber) => getTaxIdErrors(taxId).length > 0;

type TaxIdInputProps = {
  taxId: TaxIdentificationNumber;
  className: string;
  onChange: (taxId: string) => void;
  disabled?: boolean;
};

const TaxIdInput = (props: TaxIdInputProps) => {
  const {taxId, className, onChange, disabled} = props;
  const errorMenuAnchorRef = useRef<HTMLDivElement>(null);
  const [hasBeenBlurred, setHasBeenBlurred] = useState(false);
  const isTaxIdWithNoAgentNumbers = taxId instanceof TaxIdentificationNumberWithNoAgentNumbers;
  const showErrors = (hasBeenBlurred || isTaxIdWithNoAgentNumbers) && isInTaxIdErrorState(taxId);
  const [showErrorPopup, setShowErrorPopup] = useState(false);

  useEffect(() => {
    if (isTaxIdWithNoAgentNumbers) setShowErrorPopup(true);
    else setShowErrorPopup(false);
  }, [isTaxIdWithNoAgentNumbers]);

  return (
    <FormControl className={className} error={showErrors} fullWidth>
      <TextField
        variant="outlined"
        placeholder="XXX-XX-XXXX"
        autoComplete="off"
        label="Tax Identification Number"
        inputProps={{
          maxLength: 11,
        }}
        ref={errorMenuAnchorRef}
        error={showErrors}
        value={maskTaxId(taxId.value)}
        InputProps={{
          endAdornment: taxId instanceof ValidatingTaxIdentificationNumber ? <Loading /> : null,
        }}
        onFocus={event => {
          if (hasBeenBlurred && showErrors) {
            setShowErrorPopup(true);
          }
        }}
        onChange={e => onChange(unMaskTaxId(e.target.value))}
        onBlur={() => {
          setHasBeenBlurred(true);
          setShowErrorPopup(false);
        }}
        disabled={!!disabled}
      />
      <ErrorPopover
        open={showErrorPopup}
        anchor={errorMenuAnchorRef.current ?? undefined}
        errors={Errors.standardErrors({
          value: showErrors ? getTaxIdErrors(taxId) : [],
        })}
      />
    </FormControl>
  );
};

export default TaxIdInput;
